/**
 * Proposal view — printable client-facing fee disclosure document.
 * Inbox's Header and Footer are hidden in print mode (the calculator's
 * own <style> block already handles `.print:hidden`; the FeeCalculator
 * shell extends those rules to also hide AppShell chrome). The Optimum
 * logo (LOGO_DATA_URL) is intentionally retained per spec.
 */

import React from "react";
import { C } from "./brand";
import { fmtZAR, fmtPct, todayStr, TRANSFER_TYPES } from "./feeEngine";
import { LOGO_DATA_URL } from "./logo";

export default function Proposal({ data, settings, onBack }) {
  if (!data) return null;
  const { result, clientName, productName, inputs } = data;
  const handlePrint = () => window.print();

  return (
    <div className="proposal-view">
      <div className="print:hidden flex justify-between items-center mb-6">
        <button
          onClick={onBack}
          style={{
            background: "transparent",
            color: C.teal,
            border: `1px solid ${C.border}`,
            padding: "8px 14px",
            borderRadius: 4,
            fontSize: 13,
            fontWeight: 500,
            cursor: "pointer",
          }}
        >
          ← Back to calculator
        </button>
        <button
          onClick={handlePrint}
          style={{
            background: C.tealDark,
            color: C.white,
            border: "none",
            padding: "10px 18px",
            borderRadius: 4,
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          🖨 Print / Save as PDF
        </button>
      </div>

      <div
        className="proposal-doc"
        style={{
          background: C.white,
          border: `1px solid ${C.border}`,
          padding: "48px 56px",
          maxWidth: 800,
          margin: "0 auto",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        }}
      >
        {/* Header */}
        <div className="flex justify-between items-start" style={{ marginBottom: 40 }}>
          <img src={LOGO_DATA_URL} alt="Optimum" style={{ height: 56, width: "auto" }} />
          <div style={{ textAlign: "right", fontSize: 11, color: C.muted, lineHeight: 1.6 }}>
            <div>info@optimumgroup.co.za</div>
            <div>0861 OPTIFIN (678 4346)</div>
            <div style={{ color: C.orange, fontWeight: 600, fontStyle: "italic" }}>www.optimumgroup.co.za</div>
          </div>
        </div>

        {/* Title */}
        <div style={{ borderBottom: `2px solid ${C.tealDark}`, paddingBottom: 16, marginBottom: 28 }}>
          <div style={{ fontSize: 11, color: C.teal, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 4, fontWeight: 600 }}>
            Advice Fee Disclosure
          </div>
          <h1 style={{ fontSize: 28, color: C.tealDark, fontWeight: 600, margin: 0 }}>Fee Proposal</h1>
        </div>

        {/* Client / date block */}
        <div className="grid grid-cols-2 gap-6" style={{ marginBottom: 28 }}>
          <div>
            <Lbl>Prepared for</Lbl>
            <Val>{clientName || "—"}</Val>
          </div>
          <div>
            <Lbl>Date</Lbl>
            <Val>{todayStr()}</Val>
          </div>
          <div>
            <Lbl>Investment</Lbl>
            <Val>{productName || "—"}</Val>
          </div>
          <div>
            <Lbl>Contribution</Lbl>
            <Val>
              {(() => {
                let lump = inputs.hasLumpSum && inputs.lumpSumAmount ? fmtZAR(inputs.lumpSumAmount) : null;
                if (lump && inputs.transferType === TRANSFER_TYPES.SECTION14) lump = `${lump} — Section 14 transfer`;
                else if (lump && inputs.transferType === TRANSFER_TYPES.DIRECTIVE135) lump = `${lump} — Directive 135 transfer`;
                else if (lump && inputs.transferType === TRANSFER_TYPES.EXISTING) lump = `${lump} — existing investment`;
                else if (lump) lump = `${lump} lump sum`;
                const monthly = inputs.hasRecurring && inputs.monthlyAmount
                  ? `${fmtZAR(inputs.monthlyAmount)} / month`
                  : null;
                if (lump && monthly) return `${lump} + ${monthly}`;
                return lump || monthly || "—";
              })()}
            </Val>
          </div>
        </div>

        {/* Initial fee block */}
        {result.initial.applies && (
          <div style={{ marginBottom: 28 }}>
            <SectionTitle>Initial advice fee</SectionTitle>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 12, lineHeight: 1.6 }}>
              Calculated on a time-based fee model — {inputs.hours} hour{inputs.hours === 1 ? "" : "s"} at the
              prevailing hourly rate. Once-off, deducted at the time of investment.
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <tbody>
                <Row
                  label="Initial fee (% of lump sum)"
                  value={fmtPct(result.initial.gross / (result.initial.lumpSumAmount || 1), 2)}
                />
                <Row label="Initial fee (excl. VAT)" value={fmtZAR(result.initial.gross)} />
                <Row label={`VAT @ ${fmtPct(settings.vatRate, 0)}`} value={fmtZAR(result.initial.vat)} />
                <Row label="Total payable" value={fmtZAR(result.initial.total)} total />
              </tbody>
            </table>
          </div>
        )}

        {!result.initial.applies && (
          <div style={{ marginBottom: 28 }}>
            <SectionTitle>Initial advice fee</SectionTitle>
            <div style={{ fontSize: 13, color: C.text, lineHeight: 1.7 }}>
              <strong>No initial fee.</strong> {result.initial.reason}
            </div>
          </div>
        )}

        {/* Ongoing fee block */}
        {result.ongoing.rate > 0 && (
          <div style={{ marginBottom: 28 }}>
            <SectionTitle>Ongoing advice fee</SectionTitle>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 12, lineHeight: 1.6 }}>
              Charged as a percentage of assets under advice, deducted monthly by the platform and paid to the practice.
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <tbody>
                <Row label="Annual rate" value={`${result.ongoing.display} + VAT`} />
                {result.ongoing.annualTotal !== undefined && (
                  <>
                    <Row label="Annual fee (excl. VAT)" value={fmtZAR(result.ongoing.annualGross)} />
                    <Row label={`VAT @ ${fmtPct(settings.vatRate, 0)}`} value={fmtZAR(result.ongoing.annualVat)} />
                    <Row label="Annual total (estimate)" value={fmtZAR(result.ongoing.annualTotal)} total />
                    <Row label="Monthly equivalent" value={fmtZAR(result.ongoing.monthlyTotal)} />
                  </>
                )}
              </tbody>
            </table>
            {result.ongoing.annualTotal !== undefined && (
              <div style={{ fontSize: 11, color: C.muted, marginTop: 8, fontStyle: "italic", lineHeight: 1.6 }}>
                {result.ongoing.combined
                  ? `Estimate based on year-1 capital — lump sum plus 12 months of contributions (${fmtZAR(result.ongoing.base)}). No growth assumed; actual fees fluctuate with portfolio value.`
                  : "Estimate based on the current investment amount. Actual fees fluctuate with portfolio value."}
              </div>
            )}
          </div>
        )}

        {/* Notes */}
        <div style={{ background: C.bgSoft, padding: 16, borderRadius: 4, marginBottom: 28 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: C.teal, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
            Notes
          </div>
          <ul style={{ fontSize: 12, color: C.text, lineHeight: 1.7, margin: 0, paddingLeft: 18 }}>
            <li>All fees are quoted in South African Rand and include VAT where indicated.</li>
            <li>Fees are deducted by the product provider and paid to Optimum Professional Financial Planning.</li>
            <li>This proposal is a fee disclosure only and does not constitute investment advice.</li>
          </ul>
        </div>

        {/* Tagline */}
        <div style={{ textAlign: "center", marginBottom: 28, color: C.teal, fontSize: 12, fontStyle: "italic" }}>
          where <strong style={{ fontStyle: "italic" }}>possibility</strong> runs deep
        </div>

        {/* Compliance footer */}
        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 16, fontSize: 10, color: C.teal, lineHeight: 1.6 }}>
          <div style={{ fontWeight: 600 }}>Optimum Professional Financial Planning (Pty) Ltd</div>
          <div>Registration Number: 2009/001944/07</div>
          <div>Authorised Financial Services Provider | 8143</div>
        </div>
      </div>
    </div>
  );
}

function Lbl({ children }) {
  return (
    <div style={{ fontSize: 10, color: C.teal, textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 600, marginBottom: 4 }}>
      {children}
    </div>
  );
}

function Val({ children }) {
  return <div style={{ fontSize: 14, color: C.text, fontWeight: 500 }}>{children}</div>;
}

function SectionTitle({ children }) {
  return (
    <h3 style={{ fontSize: 14, fontWeight: 600, color: C.tealDark, margin: "0 0 10px 0", textTransform: "uppercase", letterSpacing: 0.5 }}>
      {children}
    </h3>
  );
}

function Row({ label, value, total }) {
  return (
    <tr style={total ? { borderTop: `1px solid ${C.tealDark}`, fontWeight: 600 } : {}}>
      <td style={{ padding: "8px 0", color: total ? C.tealDark : C.text, fontSize: total ? 14 : 13 }}>{label}</td>
      <td style={{ padding: "8px 0", textAlign: "right", color: total ? C.tealDark : C.text, fontVariantNumeric: "tabular-nums", fontSize: total ? 14 : 13 }}>
        {value}
      </td>
    </tr>
  );
}
